import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { UseDataProps } from './types';
import { useStore } from '30.quickConnect.Stores';
import { debounceTime } from '50.quickConnect.Fields/const';

export const useData = (): UseDataProps => {
  // On récupère le store
  const {
    InboxesStore: {
      inboxesTotalNumber,
      PreFilledStore: {
        items: preFilled,
        expandedNodes: preFilledExpandedNodes,
        treeNodeKeys: preFilledTreeNodeKeys,
        setFilter: setPreFilledFilter,
        filter: preFilledFilter,
        setExpandedNodes: setPreFilledExpandedNodes,
        numberDisplayed: numberPreFilledDisplayed,
      },
      MyOwnTransfersStore: {
        items: myOwnTransfers,
        expandedNodes: myOwnTransfersExpandedNodes,
        treeNodeKeys: myOwnTransfersTreeNodeKeys,
        setFilter: setMyOwnTransfersFilter,
        filter: myOwnTransfersFilter,
        setExpandedNodes: setMyOwnTransfersExpandedNodes,
        numberDisplayed: numberMyOwnTransfersDisplayed,
      },
      TasksStore: {
        items: tasks,
        expandedNodes: tasksExpandedNodes,
        treeNodeKeys: tasksTreeNodeKeys,
        setFilter: setTaskFilter,
        filter: tasksFilter,
        setExpandedNodes: setTasksExpandedNodes,
        numberDisplayed: numberTasksDisplayed,
      },
      OthersStore: {
        items: others,
        expandedNodes: othersExpandedNodes,
        treeNodeKeys: othersTreeNodeKeys,
        setFilter: setOtherFilter,
        filter: otherFilter,
        setExpandedNodes: setOthersExpandedNodes,
        numberDisplayed: numberOthersDisplayed,
      },
    },
    UserSettingsStore: { inboxesAsTree, setInboxesAsTree, setInboxesAsAlphaNumericSort, inboxesAsAlphaNumericSort },
  } = useStore();

  // On définit le state
  const [filterInputValue, setFilterInputValue] = useState(
    preFilledFilter ?? myOwnTransfersFilter ?? tasksFilter ?? otherFilter,
  );
  const inboxesTreeNodeNumber = useMemo(
    () =>
      preFilledTreeNodeKeys.length +
      myOwnTransfersTreeNodeKeys.length +
      tasksTreeNodeKeys.length +
      othersTreeNodeKeys.length,
    [
      myOwnTransfersTreeNodeKeys.length,
      othersTreeNodeKeys.length,
      preFilledTreeNodeKeys.length,
      tasksTreeNodeKeys.length,
    ],
  );
  const isAllExpanded = useMemo(
    () =>
      preFilledExpandedNodes.length +
        myOwnTransfersExpandedNodes.length +
        tasksExpandedNodes.length +
        othersExpandedNodes.length <
      inboxesTreeNodeNumber,
    [
      inboxesTreeNodeNumber,
      myOwnTransfersExpandedNodes.length,
      othersExpandedNodes.length,
      preFilledExpandedNodes.length,
      tasksExpandedNodes.length,
    ],
  );

  const handleExpandClick = useCallback(() => {
    setPreFilledExpandedNodes(isAllExpanded ? preFilledTreeNodeKeys : []);
    setMyOwnTransfersExpandedNodes(isAllExpanded ? myOwnTransfersTreeNodeKeys : []);
    setTasksExpandedNodes(isAllExpanded ? tasksTreeNodeKeys : []);
    setOthersExpandedNodes(isAllExpanded ? othersTreeNodeKeys : []);
  }, [
    isAllExpanded,
    myOwnTransfersTreeNodeKeys,
    othersTreeNodeKeys,
    preFilledTreeNodeKeys,
    setMyOwnTransfersExpandedNodes,
    setOthersExpandedNodes,
    setPreFilledExpandedNodes,
    setTasksExpandedNodes,
    tasksTreeNodeKeys,
  ]);

  const applyFilter = useCallback((): void => {
    setPreFilledFilter(filterInputValue);
    setMyOwnTransfersFilter(filterInputValue);
    setTaskFilter(filterInputValue);
    setOtherFilter(filterInputValue);
  }, [filterInputValue, setMyOwnTransfersFilter, setOtherFilter, setPreFilledFilter, setTaskFilter]);

  useEffect(() => {
    if (preFilledFilter !== '' && myOwnTransfersFilter !== '' && tasksFilter !== '' && otherFilter !== '') {
      setPreFilledExpandedNodes(preFilledTreeNodeKeys);
      setMyOwnTransfersExpandedNodes(myOwnTransfersTreeNodeKeys);
      setTasksExpandedNodes(tasksTreeNodeKeys);
      setOthersExpandedNodes(othersTreeNodeKeys);
    }
  }, [
    otherFilter,
    othersTreeNodeKeys,
    preFilledFilter,
    preFilledTreeNodeKeys,
    setOthersExpandedNodes,
    setPreFilledExpandedNodes,
    setMyOwnTransfersExpandedNodes,
    setTasksExpandedNodes,
    tasksFilter,
    tasksTreeNodeKeys,
    myOwnTransfersFilter,
    myOwnTransfersTreeNodeKeys,
  ]);

  const clearFilter = useCallback((): void => {
    setFilterInputValue('');
    setPreFilledFilter('');
    setMyOwnTransfersFilter('');
    setTaskFilter('');
    setOtherFilter('');
  }, [setOtherFilter, setPreFilledFilter, setMyOwnTransfersFilter, setTaskFilter]);

  const numberItemsDisplayed = useMemo(
    () => numberPreFilledDisplayed + numberMyOwnTransfersDisplayed + numberTasksDisplayed + numberOthersDisplayed,
    [numberPreFilledDisplayed, numberMyOwnTransfersDisplayed, numberTasksDisplayed, numberOthersDisplayed],
  );

  const debouncedSearch = useDebouncedCallback((valueToSearch) => {
    setPreFilledFilter(valueToSearch);
    setMyOwnTransfersFilter(valueToSearch);
    setTaskFilter(valueToSearch);
    setOtherFilter(valueToSearch);
  }, debounceTime);

  useEffect(() => {
    if (filterInputValue) {
      debouncedSearch(filterInputValue);
    }
  }, [debouncedSearch, filterInputValue]);

  return {
    applyFilter,
    clearFilter,
    filterInputValue,
    setFilterInputValue,
    inboxFilter: preFilledFilter ?? myOwnTransfersFilter ?? tasksFilter ?? otherFilter,
    handleExpandClick,
    isAllExpanded,
    isAllExpandedDisabled: inboxesTreeNodeNumber === 0,
    numberItemsDisplayed,
    inboxesTotalNumber,
    inboxesAsTree,
    setInboxesAsTree,
    setInboxesAsAlphaNumericSort,
    inboxesAsAlphaNumericSort,
  };
};
